import React, { useCallback, useEffect, useState } from 'react';
import FundFamilyApi from '../../../../../core/apis/FundFamilyApi';
import FundApi from '../../../../../core/apis/FundApi';
import FundInvestorTagApi from '../../../../../core/apis/FundInvestorTagApi';
import FundTagApi from '../../../../../core/apis/FundTagApi';
import useNotifyServerError from '../../../../../core/hooks/useNotifyServerError';
import FundInvestorTagLevelTree from './FundInvestorTagLevelTree';
import { Form } from 'react-final-form';
import { Grid } from '@material-ui/core';
import { Loading } from 'ra-ui-materialui';
import { NotificationForJapanFund } from '../FundLevelTree';
import ResourceUploadApi from '../../../../../core/apis/ResourceUploadApi';

function buildDataStructure(fundFamilies, funds, fiTags) {
  const data = [];
  funds.forEach(fund => {
    const fundNode = {
      nodeId: fund.id,
      fundId: fund.id,
      fundName: fund.name,
      fundTags: fund.fundTags,
      fundNode: true,
      fundInvestorTags: [],
      ...fund
    };

    // Find Associated fundInvestorsTags for the fund
    fundNode.fundInvestorTags = fiTags.reduce((acc, fiTag) => {
      if (fiTag.fundInvestors.some(fi => fi.fundId === fund.id)) {
        return [
          ...acc,
          {
            nodeId: `${fund.id}:${fiTag.id}`,
            id: fiTag.id,
            name: fiTag.name,
            fundName: fund.name,
            fundId: fund.id,
            fundInvestorTagNode: true
          }
        ];
      }
      return acc;
    }, []);

    const existingFundFamilyNode = data.find(
      node => !node.fundNode && node.fundFamilyId === fund.fundFamilyId
    );

    if (existingFundFamilyNode) {
      existingFundFamilyNode.funds.push(fundNode);
    } else {
      const fundFamily = fundFamilies.find(ff => ff.id === fund.fundFamilyId);
      if (fundFamily) {
        const familyNode = {
          nodeId: fundFamily.id,
          fundFamilyId: fundFamily.id,
          fundFamilyName: fundFamily.name,
          funds: [fundNode],
          fundFamilyNode: true
        };
        data.push(familyNode);
      } else {
        data.push(fundNode);
      }
    }
  });
  return data;
}

const FundInvestorTagLevelTreeContainer = ({
  classes,
  resourceupload,
  onFormInit,
  onSubmitEnd
}) => {
  const [leftList, setLeftList] = useState([]);
  const [data, setData] = useState([]);
  const [fundFamilies, setFundFamilies] = useState([]);
  const [fundTags, setFundTags] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isPristine, setIsPristine] = useState(true);

  const [isJapanFundSelected, setIsJapanFundSelected] = useState(false);

  const notifyServerError = useNotifyServerError();

  const stepReadOnly = !!(
    resourceupload.uploadVia && resourceupload.uploadVia === 'API'
  );

  const fetchData = useCallback(async () => {
    const fundFamilyPromise = FundFamilyApi.getAll();
    const fundsPromise = FundApi.getAll();
    const fundInvestorTagsPromise = FundInvestorTagApi.getAll();
    const fundTagsPromise = FundTagApi.getAll();

    await Promise.all([
      fundFamilyPromise,
      fundsPromise,
      fundInvestorTagsPromise,
      fundTagsPromise
    ])
      .then(
        ([
          fundFamiliesResponse,
          fundsResponse,
          fundInvestorTagsResp,
          fundTagsResponse
        ]) => {
          setFundFamilies(fundFamiliesResponse.pageData);
          setFundTags(fundTagsResponse.pageData);
          setData(
            buildDataStructure(
              fundFamiliesResponse.pageData,
              fundsResponse.pageData,
              fundInvestorTagsResp.pageData
            )
          );
          setLeftList(
            buildDataStructure(
              fundFamiliesResponse.pageData,
              fundsResponse.pageData,
              fundInvestorTagsResp.pageData
            )
          );
        }
      )
      .catch(e => notifyServerError(e, undefined))
      .finally(() => setIsLoading(false));
  }, [notifyServerError]);

  const validate = values => {
    const errors = {};
    if (!values.fundInvestorTagId && values.fundIds.length === 0) {
      errors.required =
        'At least one Fund or Fund-Investor Tag must be selected';
    }

    return errors;
  };

  const onSubmit = async values => {
    if (isPristine) {
      onSubmitEnd(resourceupload);
    } else {
      try {
        const formData = new FormData();
        formData.append('resourceUploadId', resourceupload.resourceUploadId);
        formData.append('metadata', JSON.stringify(values));
        const updatedResourceUpload = await ResourceUploadApi.updateResourceUpload(
          formData
        );
        onSubmitEnd(updatedResourceUpload);
      } catch (e) {
        notifyServerError(e);
      }
    }
  };

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  if (isLoading) return <Loading />;

  return (
    <Form
      onSubmit={onSubmit}
      initialValues={resourceupload}
      validate={validate}
    >
      {({ handleSubmit, form, errors, pristine, modified, submitFailed }) => {
        onFormInit(form);
        setIsPristine(pristine);
        return (
          <form onSubmit={handleSubmit} style={{ width: '100%' }}>
            <Grid container style={{ height: '100%', width: '100%' }}>
              <Grid
                item
                xs={12}
                style={{ height: isJapanFundSelected ? '95%' : '100%' }}
              >
                <Grid
                  container
                  justifyContent="space-between"
                  alignItems="center"
                  style={{ height: '100%' }}
                >
                  <FundInvestorTagLevelTree
                    classes={classes}
                    leftList={leftList}
                    setLeftList={setLeftList}
                    data={data}
                    fundFamilies={fundFamilies}
                    fundTags={fundTags}
                    setIsJapanFundSelected={setIsJapanFundSelected}
                    stepReadOnly={stepReadOnly}
                    resourceupload={resourceupload}
                    formRenderProps={{ errors, modified, submitFailed }}
                  />
                </Grid>
              </Grid>
              <Grid
                item
                xs={12}
                style={{ display: 'flex', alignSelf: 'normal' }}
              >
                {isJapanFundSelected ? <NotificationForJapanFund /> : null}
              </Grid>
            </Grid>
          </form>
        );
      }}
    </Form>
  );
};

export default FundInvestorTagLevelTreeContainer;
